import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Coins, Loader2, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { isAnimatedGift } from "@/components/GiftAnimation";

/**
 * Reusable gift picker. `onSend(giftKey, {message}) => Promise` handles the actual send.
 * `context` is one of: 'post' | 'live' | 'dm'. Controls whether the message field is shown.
 */
export default function GiftPicker({ open, onOpenChange, onSend, context = "post", recipientName, wheel = null, onOpenWheel = null }) {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setMessage("");
    (async () => {
      try {
        const { data } = await api.get("/gifts/catalog");
        setCatalog(data);
      } catch (e) {
        // Fallback local list so the modal never blocks
        setCatalog([
          { key: "rose", label: "Rose", emoji: "🌹", coins: 1 },
          { key: "fire", label: "Fire", emoji: "🔥", coins: 5 },
        ]);
      }
    })();
  }, [open]);

  const coins = user?.coins || 0;

  const send = async () => {
    if (!selected || busy) return;
    if (coins < selected.coins) {
      toast.error("Not enough coins.");
      return;
    }
    setBusy(true);
    try {
      await onSend(selected.key, context !== "post" ? message.trim() || null : null);
      toast.success(`${selected.emoji} ${selected.label} sent!`);
      if (refresh) refresh();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send gift.");
    } finally {
      setBusy(false);
    }
  };

  const contextLabel = useMemo(() => {
    if (context === "live") return "Live stream";
    if (context === "dm") return "Direct message";
    return "Post";
  }, [context]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass rounded-none max-w-md border-white/10 max-h-[90vh] overflow-y-auto" data-testid="gift-picker-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles size={18} className="text-rose-500" />
            Send a gift
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            {contextLabel}{recipientName ? ` · to @${recipientName}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between text-xs uppercase tracking-widest">
          <span className="text-zinc-500">Your balance</span>
          <span className="flex items-center gap-1.5 text-amber-400">
            <Coins size={14} /> {coins.toLocaleString()} coins
          </span>
        </div>

        {/* Creator's prize wheel entry (Live only, when the creator has it ON). */}
        {context === "live" && (
          <Link
            to="/vip"
            data-testid="gift-picker-vip-link"
            onClick={() => onOpenChange(false)}
            className="w-full flex items-center justify-between border border-white/10 bg-black/40 hover:border-rose-500/50 px-3 py-2 text-left transition-colors"
          >
            <span className="text-xs text-zinc-300">👑 Monthly Panda VIP badges — unlocked by coin purchases</span>
            <span className="text-[10px] uppercase tracking-widest text-rose-400">How it works</span>
          </Link>
        )}
        {context === "live" && wheel?.enabled && onOpenWheel && (
          <button
            type="button"
            data-testid="gift-picker-wheel"
            onClick={() => { onOpenChange(false); onOpenWheel(); }}
            className="w-full flex items-center justify-between border border-yellow-400/40 bg-yellow-400/10 px-3 py-2 text-left"
          >
            <span className="text-sm text-white">🎡 {wheel.name || "Prize wheel"} <span className="text-zinc-400">· {(wheel.segments || []).length} prizes</span></span>
            <span className="text-xs uppercase tracking-widest text-yellow-300">Spin · {Number(wheel.price || 25).toLocaleString()} coins</span>
          </button>
        )}

        {/* Category tabs — kept flat visually but grouped so 34 gifts stay browsable. */}
        <GiftCategoryTabs
          catalog={catalog}
          coins={coins}
          selected={selected}
          onSelect={setSelected}
        />

        {context !== "post" && (
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a message (optional)"
            maxLength={140}
            className="bg-black/40 border-white/10 rounded-none py-5 text-sm"
            data-testid="gift-message"
          />
        )}

        <div className="flex items-center justify-between pt-2">
          <Link
            to="/coins"
            onClick={() => onOpenChange(false)}
            className="text-xs uppercase tracking-widest text-zinc-500 hover:text-rose-500"
            data-testid="gift-topup-link"
          >
            + Top up coins
          </Link>
          <Button
            onClick={send}
            disabled={!selected || busy}
            className="btn-crimson rounded-none px-5 py-5"
            data-testid="gift-send-button"
          >
            {busy ? (
              <><Loader2 size={14} className="animate-spin mr-2" /> Sending…</>
            ) : selected ? (
              <>Send {selected.emoji} · {selected.coins} coins</>
            ) : (
              "Pick a gift"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Category tabs — Live Menu is the star, but Classic / Buzz / DMs stay one tap away. */
function GiftCategoryTabs({ catalog, coins, selected, onSelect }) {
  const groups = useMemo(() => {
    const cat = (g) => g.category || "classic";
    const order = ["live_menu", "buzz", "dms", "classic"];
    const labelMap = {
      live_menu: "Live Menu",
      buzz: "Buzz",
      dms: "DMs",
      classic: "Classic",
    };
    const buckets = {};
    for (const g of catalog) {
      const k = cat(g);
      if (!buckets[k]) buckets[k] = [];
      buckets[k].push(g);
    }
    return order
      .filter((k) => buckets[k]?.length)
      .map((k) => ({ key: k, label: labelMap[k], items: buckets[k] }));
  }, [catalog]);
  const [active, setActive] = useState(() => groups[0]?.key || "classic");
  // If the catalog reloads and our tab vanishes, snap to the first.
  useEffect(() => {
    if (groups.length && !groups.find((g) => g.key === active)) {
      setActive(groups[0].key);
    }
  }, [groups, active]);
  const shown = groups.find((g) => g.key === active)?.items || [];
  return (
    <>
      <div className="flex gap-1 border-b border-white/10 -mx-6 px-6 pb-1" data-testid="gift-tabs">
        {groups.map((g) => (
          <button
            key={g.key}
            type="button"
            data-testid={`gift-tab-${g.key}`}
            onClick={() => setActive(g.key)}
            className={`px-3 py-2 text-[11px] uppercase tracking-widest transition-colors ${
              active === g.key
                ? "text-rose-400 border-b-2 border-rose-500"
                : "text-zinc-500 hover:text-white"
            }`}
          >
            {g.label} <span className="opacity-60 ml-1">{g.items.length}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-2" data-testid="gift-grid">
        {shown.map((g) => {
          const isSel = selected?.key === g.key;
          const canAfford = coins >= g.coins;
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => canAfford && onSelect(g)}
              disabled={!canAfford}
              data-testid={`gift-tile-${g.key}`}
              className={`relative flex flex-col items-center gap-1 py-3 border transition-all ${
                isSel
                  ? "bg-rose-600/20 border-rose-500 shadow-[0_0_0_1px_rgba(244,63,94,0.6)]"
                  : canAfford
                    ? "bg-black/30 border-white/10 hover:border-white/30"
                    : "bg-black/20 border-white/5 opacity-40 cursor-not-allowed"
              }`}
            >
              {isAnimatedGift(g.key) && (
                <span
                  className="absolute top-1 right-1 text-[8px] uppercase tracking-widest bg-gradient-to-r from-amber-400 to-rose-500 text-black font-bold px-1 py-[1px] rounded-sm"
                  data-testid={`gift-anim-badge-${g.key}`}
                >
                  ✦ anim
                </span>
              )}
              <div className="text-3xl leading-none">{g.emoji}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-400 text-center leading-tight px-1">
                {g.label}
              </div>
              <div className="text-[11px] text-amber-400 flex items-center gap-0.5">
                <Coins size={10} /> {g.coins}
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}
